import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ArrowLeft, Shield, Mail, Phone, ChevronDown, ChevronUp, Download, Trash2, User, Bell, Eye, Lock, Clock, Share2, Cookie, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const PrivacyPolicy = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const [openSections, setOpenSections] = useState({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDataModal, setShowDataModal] = useState(false);

    const toggleSection = (index) => {
        setOpenSections(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const sections = [
        {
            icon: <User className="w-5 h-5" />,
            title: "Quem é Responsável pelos Seus Dados?",
            content: `A **Paula Pontvianne Consultoria em Comunicação Empática** é a controladora dos seus dados pessoais.

Nosso Encarregado de Proteção de Dados (DPO) é **Aislan Munin Pavanello**, que pode ser contatado através do e-mail: dpo@paulapontvianne.com.br

Estamos comprometidos com a proteção dos seus dados pessoais em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei 13.709/18).`
        },
        {
            icon: <Eye className="w-5 h-5" />,
            title: "Que Dados Coletamos?",
            content: `Coletamos os seguintes tipos de dados:

• **Dados de identificação**: nome, e-mail, foto de perfil (quando fornecida via login social)
• **Dados de acesso**: logs de acesso, endereço IP, data e hora de acesso
• **Dados de uso**: progresso nas lições, respostas em exercícios, interações em simulações com IA
• **Dados de pagamento**: processados de forma segura através do Stripe (não armazenamos dados completos de cartão)
• **Dados de preferências**: configurações de conta e preferências de comunicação

**Dados Sensíveis** (tratados com cuidado especial e base legal de consentimento explícito):
• Conteúdo do Diário Emocional: reflexões pessoais e registros emocionais inseridos voluntariamente
• Respostas em questionários de bem-estar e avaliações psicológicas
• Registros de check-ins emocionais e indicadores de estado emocional
• Reflexões geradas durante simulações de comunicação com IA

Estes dados sensíveis são utilizados exclusivamente para personalizar sua experiência de aprendizado e nunca serão compartilhados sem sua autorização explícita.`
        },
        {
            icon: <Bell className="w-5 h-5" />,
            title: "Como Usamos Seus Dados?",
            content: `Utilizamos seus dados para:

• **Personalização**: Oferecer experiência de aprendizado adaptada ao seu progresso
• **Melhorias**: Desenvolver e aprimorar nossos conteúdos e funcionalidades
• **Comunicação**: Enviar atualizações importantes sobre sua conta e novos conteúdos
• **Análise**: Gerar relatórios de progresso e insights sobre seu desenvolvimento
• **Segurança**: Proteger sua conta contra acessos não autorizados
• **Obrigações legais**: Cumprir requisitos legais e regulatórios

**Política de Inteligência Artificial**: Utilizamos IA (Claude/Anthropic) para funcionalidades como simuladores de conversa e geração de insights. **Não utilizamos seus dados pessoais ou dados sensíveis para treinar modelos de IA.** Os dados enviados à IA são processados exclusivamente para gerar a resposta imediata e não são retidos pelo provedor para fins de treinamento.`
        },
        {
            icon: <AlertCircle className="w-5 h-5" />,
            title: "Diferenças entre Clientes Individuais e Empresariais",
            content: `**Para clientes individuais (B2C)**:
• Você é o único responsável por seus dados
• Pode exercer todos os direitos da LGPD diretamente conosco
• Seus dados não são compartilhados com terceiros sem consentimento

**Para clientes empresariais (B2B)**:
• A empresa contratante (gestora da conta) pode acessar **relatórios agregados e anonimizados** de progresso do grupo
• Esses relatórios mostram médias, totais e tendências gerais — **nunca dados individuais identificáveis**
• O administrador da conta empresarial pode gerenciar usuários do grupo
• Dados pessoais e dados sensíveis dos usuários individuais **não são acessíveis** pelo gestor da empresa
• Políticas adicionais podem se aplicar conforme contrato empresarial`
        },
        {
            icon: <Lock className="w-5 h-5" />,
            title: "Como Protegemos Seus Dados?",
            content: `Implementamos diversas medidas de segurança:

• **Criptografia**: Dados em trânsito protegidos por HTTPS/TLS
• **Armazenamento seguro**: Google Cloud Platform (Cloud Run + Firebase Firestore), com criptografia avançada em repouso e em trânsito
• **Autenticação segura**: Firebase Authentication com padrões de segurança avançados
• **Pagamentos seguros**: Processamento via Stripe (certificado PCI-DSS)
• **Controle de acesso**: Baseado em funções e princípio do menor privilégio
• **Monitoramento**: Sistema de detecção de ameaças e anomalias`
        },
        {
            icon: <Share2 className="w-5 h-5" />,
            title: "Quando Compartilhamos Dados?",
            content: `Seus dados podem ser compartilhados apenas com:

• **Stripe**: Para processamento seguro de pagamentos
• **Google (Firebase/Cloud)**: Para autenticação e armazenamento
• **Autoridades competentes**: Quando exigido por lei ou ordem judicial

**NÃO vendemos seus dados** e não os compartilhamos para fins de marketing de terceiros.`
        },
        {
            icon: <User className="w-5 h-5" />,
            title: "Seus Direitos e Controles",
            content: `Conforme a LGPD, você tem direito a:

• **Confirmação**: Saber se tratamos seus dados
• **Acesso**: Obter cópia dos seus dados pessoais
• **Correção**: Atualizar dados incompletos ou incorretos
• **Anonimização/Bloqueio**: Solicitar anonimização de dados desnecessários
• **Portabilidade**: Receber seus dados em formato estruturado
• **Eliminação**: Solicitar exclusão dos seus dados
• **Informação**: Saber com quem compartilhamos seus dados
• **Revogação**: Retirar seu consentimento a qualquer momento

Para exercer qualquer direito, use os botões de "Ações Rápidas" acima ou contate nosso DPO.`
        },
        {
            icon: <Cookie className="w-5 h-5" />,
            title: "Cookies e Tecnologias Similares",
            content: `Utilizamos cookies para:

• **Cookies essenciais**: Manter você conectado e garantir funcionamento básico
• **Cookies de preferência**: Salvar suas configurações
• **Cookies analíticos**: Entender como você usa a plataforma

Você pode configurar seu navegador para recusar cookies, mas isso pode afetar algumas funcionalidades.`
        },
        {
            icon: <Clock className="w-5 h-5" />,
            title: "Por Quanto Tempo Mantemos Seus Dados?",
            content: `Mantemos seus dados enquanto:

• Sua conta estiver ativa
• Houver obrigação legal de retenção
• For necessário para fins legítimos (como defesa em processos)

Após solicitar exclusão, seus dados são removidos em até 30 dias, exceto quando a lei exigir retenção por período maior.

Dados de pagamento são mantidos pelo período exigido pela legislação fiscal.`
        }
    ];

    const handleRequestData = async () => {
        setShowDataModal(true);
        // Em produção, isso enviaria um e-mail ao usuário com seus dados
    };

    const handleRequestDeletion = async () => {
        setShowDeleteModal(true);
    };

    const handleContactDPO = () => {
        window.location.href = 'mailto:dpo@paulapontvianne.com.br?subject=Solicitação LGPD - ' + (user?.email || 'Visitante');
    };

    return (
        <div className={`min-h-screen ${isAuthenticated ? 'bg-gray-50' : 'bg-gradient-to-b from-slate-900 via-purple-950 to-slate-900'}`}>
            {/* Header for non-authenticated */}
            {!isAuthenticated && (
                <>
                    <div className="fixed inset-0 pointer-events-none">
                        <div className="absolute top-20 left-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
                        <div className="absolute bottom-20 right-10 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl" />
                    </div>
                    <header className="relative z-50 py-6 border-b border-white/10">
                        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="flex items-center justify-between">
                                <Button
                                    variant="ghost"
                                    className="text-white/80 hover:text-white hover:bg-white/10"
                                    onClick={() => navigate('/')}
                                >
                                    <ArrowLeft className="w-5 h-5 mr-2" />
                                    Voltar
                                </Button>
                                <img src="/logo-simby.png" alt="Simby" className="h-10" />
                            </div>
                        </div>
                    </header>
                </>
            )}

            {/* Content */}
            <main className={`relative py-8 ${isAuthenticated ? 'px-4' : ''}`}>
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Title */}
                    <div className={`mb-8 ${isAuthenticated ? '' : 'text-center'}`}>
                        <div className={`flex items-center gap-3 mb-4 ${isAuthenticated ? '' : 'justify-center'}`}>
                            <Shield className={`w-8 h-8 ${isAuthenticated ? 'text-purple-600' : 'text-purple-400'}`} />
                            <h1 className={`text-3xl md:text-4xl font-bold ${isAuthenticated ? 'text-gray-900' : 'text-white'}`}>
                                Política de Privacidade
                            </h1>
                        </div>
                        <p className={`text-lg ${isAuthenticated ? 'text-gray-600' : 'text-white/60'}`}>
                            Transparência total sobre como coletamos, usamos e protegemos seus dados pessoais
                        </p>
                        <p className={`text-sm mt-2 ${isAuthenticated ? 'text-gray-400' : 'text-white/40'}`}>
                            Última atualização: {new Date().toLocaleDateString('pt-BR')}
                        </p>
                    </div>

                    {/* Quick Actions for Authenticated Users */}
                    {isAuthenticated && (
                        <Card className="mb-8 p-6 border-2 border-purple-100 bg-purple-50/50">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                    <User className="w-4 h-4 text-purple-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Ações Rápidas</h3>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                <Button
                                    variant="outline"
                                    className="border-purple-200 hover:bg-purple-50"
                                    onClick={handleRequestData}
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Gerenciar Meus Dados
                                </Button>
                                <Button
                                    variant="outline"
                                    className="border-purple-200 hover:bg-purple-50"
                                    onClick={handleContactDPO}
                                >
                                    <Mail className="w-4 h-4 mr-2" />
                                    Contatar DPO
                                </Button>
                                <Button
                                    variant="outline"
                                    className="border-red-200 text-red-600 hover:bg-red-50"
                                    onClick={handleRequestDeletion}
                                >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Solicitar Exclusão
                                </Button>
                            </div>
                        </Card>
                    )}

                    {/* Philosophy Card */}
                    <Card className={`mb-8 p-6 ${isAuthenticated ? 'bg-white border-gray-200' : 'bg-white/5 border-white/10 backdrop-blur-sm'}`}>
                        <h3 className={`text-xl font-bold mb-4 ${isAuthenticated ? 'text-gray-900' : 'text-white'}`}>
                            Nossa Filosofia de Privacidade
                        </h3>
                        <p className={`leading-relaxed ${isAuthenticated ? 'text-gray-600' : 'text-white/70'}`}>
                            Na Paula Pontvianne Consultoria em Comunicação Empática, acreditamos que seus dados pessoais são seus. Nossa missão é
                            desenvolver sua inteligência emocional e relacional de forma ética, transparente e segura.
                        </p>
                        <p className={`mt-4 leading-relaxed ${isAuthenticated ? 'text-gray-600' : 'text-white/70'}`}>
                            Esta política explica de forma clara como tratamos suas informações pessoais, sempre respeitando a Lei Geral
                            de Proteção de Dados (LGPD) e suas escolhas.
                        </p>
                    </Card>

                    {/* Accordion Sections */}
                    <div className="space-y-3">
                        {sections.map((section, index) => (
                            <Card
                                key={index}
                                className={`overflow-hidden transition-all duration-200 ${isAuthenticated
                                        ? 'bg-white border-gray-200 hover:border-purple-200'
                                        : 'bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10'
                                    }`}
                            >
                                <button
                                    onClick={() => toggleSection(index)}
                                    className={`w-full p-5 flex items-center justify-between text-left transition-colors ${isAuthenticated ? 'hover:bg-gray-50' : 'hover:bg-white/5'
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isAuthenticated ? 'bg-purple-100 text-purple-600' : 'bg-purple-500/20 text-purple-400'
                                            }`}>
                                            {section.icon}
                                        </div>
                                        <span className={`font-semibold ${isAuthenticated ? 'text-gray-900' : 'text-white'}`}>
                                            {section.title}
                                        </span>
                                    </div>
                                    {openSections[index] ? (
                                        <ChevronUp className={`w-5 h-5 ${isAuthenticated ? 'text-gray-400' : 'text-white/50'}`} />
                                    ) : (
                                        <ChevronDown className={`w-5 h-5 ${isAuthenticated ? 'text-gray-400' : 'text-white/50'}`} />
                                    )}
                                </button>

                                {openSections[index] && (
                                    <div className={`px-5 pb-5 pt-0 ${isAuthenticated ? 'text-gray-600' : 'text-white/70'}`}>
                                        <div className={`pt-4 border-t ${isAuthenticated ? 'border-gray-100' : 'border-white/10'}`}>
                                            <div className="whitespace-pre-line leading-relaxed"
                                                dangerouslySetInnerHTML={{
                                                    __html: section.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </Card>
                        ))}
                    </div>

                    {/* Contact Section */}
                    <Card className={`mt-8 p-6 ${isAuthenticated
                            ? 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200'
                            : 'bg-gradient-to-r from-purple-600/20 to-indigo-600/20 border-white/10 backdrop-blur-xl'
                        }`}>
                        <div className="flex items-center gap-2 mb-4">
                            <Mail className={`w-5 h-5 ${isAuthenticated ? 'text-purple-600' : 'text-purple-400'}`} />
                            <h3 className={`text-lg font-bold ${isAuthenticated ? 'text-gray-900' : 'text-white'}`}>
                                Contato e Atualizações
                            </h3>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <p className={`font-medium mb-2 ${isAuthenticated ? 'text-gray-900' : 'text-white'}`}>
                                    <Phone className="w-4 h-4 inline mr-2" />
                                    Como Entrar em Contato:
                                </p>
                                <ul className={`ml-6 space-y-1 text-sm ${isAuthenticated ? 'text-gray-600' : 'text-white/70'}`}>
                                    <li>• DPO: <a href="mailto:dpo@paulapontvianne.com.br" className="text-purple-500 hover:underline">dpo@paulapontvianne.com.br</a></li>
                                    <li>• Suporte: Através da plataforma ou e-mail de suporte</li>
                                    <li>• Dúvidas sobre privacidade: Use o botão de contato acima</li>
                                </ul>
                            </div>

                            <div>
                                <p className={`font-medium mb-2 ${isAuthenticated ? 'text-gray-900' : 'text-white'}`}>
                                    <Bell className="w-4 h-4 inline mr-2" />
                                    Atualizações desta Política:
                                </p>
                                <p className={`text-sm ${isAuthenticated ? 'text-gray-600' : 'text-white/70'}`}>
                                    Esta política pode ser atualizada ocasionalmente. Quando houver mudanças significativas, notificaremos você
                                    por e-mail ou através da plataforma. Recomendamos revisar periodicamente esta página.
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-3 mt-6">
                            <Button
                                className="bg-purple-600 hover:bg-purple-700 text-white"
                                onClick={handleContactDPO}
                            >
                                <Mail className="w-4 h-4 mr-2" />
                                Falar com DPO
                            </Button>
                            {isAuthenticated && (
                                <Button
                                    variant="outline"
                                    className="border-purple-300 hover:bg-purple-50"
                                    onClick={handleRequestData}
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Gerenciar Dados
                                </Button>
                            )}
                        </div>
                    </Card>

                    {/* Back to Home */}
                    {!isAuthenticated && (
                        <div className="mt-12 text-center">
                            <Button
                                size="lg"
                                className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white px-8 py-6 text-lg rounded-2xl shadow-xl"
                                onClick={() => navigate('/')}
                            >
                                Voltar para a Home
                            </Button>
                        </div>
                    )}
                </div>
            </main>

            {/* Data Request Modal */}
            {showDataModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <Card className="bg-white p-6 max-w-md w-full rounded-2xl">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Gerenciar Seus Dados</h3>
                        <p className="text-gray-600 mb-6">
                            Você pode solicitar uma cópia completa dos seus dados ou atualizar suas informações pessoais.
                        </p>
                        <div className="space-y-3">
                            <Button
                                className="w-full bg-purple-600 hover:bg-purple-700"
                                onClick={() => {
                                    window.location.href = `mailto:dpo@paulapontvianne.com.br?subject=Solicitação de Dados - ${user?.email}&body=Olá,%0D%0A%0D%0ASolicito uma cópia dos meus dados pessoais conforme previsto na LGPD.%0D%0A%0D%0AE-mail da conta: ${user?.email}%0D%0ANome: ${user?.name}%0D%0A%0D%0AAtenciosamente`;
                                    setShowDataModal(false);
                                }}
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Solicitar Cópia dos Dados
                            </Button>
                            <Button
                                variant="outline"
                                className="w-full"
                                onClick={() => navigate('/profile')}
                            >
                                <User className="w-4 h-4 mr-2" />
                                Atualizar Meus Dados
                            </Button>
                            <Button
                                variant="ghost"
                                className="w-full"
                                onClick={() => setShowDataModal(false)}
                            >
                                Fechar
                            </Button>
                        </div>
                    </Card>
                </div>
            )}

            {/* Delete Request Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <Card className="bg-white p-6 max-w-md w-full rounded-2xl">
                        <h3 className="text-xl font-bold text-red-600 mb-4">⚠️ Solicitar Exclusão de Dados</h3>
                        <p className="text-gray-600 mb-4">
                            Ao solicitar a exclusão, todos os seus dados serão permanentemente removidos em até 30 dias, incluindo:
                        </p>
                        <ul className="text-sm text-gray-600 mb-6 space-y-1">
                            <li>• Seu progresso em lições e exercícios</li>
                            <li>• Histórico do Diário de Gatilhos</li>
                            <li>• Dados de simulações com IA</li>
                            <li>• Informações de perfil e preferências</li>
                        </ul>
                        <p className="text-sm text-gray-500 mb-6">
                            Alguns dados podem ser mantidos por obrigação legal (ex: dados fiscais).
                        </p>
                        <div className="space-y-3">
                            <Button
                                className="w-full bg-red-600 hover:bg-red-700"
                                onClick={() => {
                                    window.location.href = `mailto:dpo@paulapontvianne.com.br?subject=Solicitação de EXCLUSÃO - ${user?.email}&body=Olá,%0D%0A%0D%0ASolicito a exclusão completa dos meus dados pessoais conforme previsto na LGPD.%0D%0A%0D%0AEntendo que esta ação é irreversível e que meus dados serão removidos em até 30 dias.%0D%0A%0D%0AE-mail da conta: ${user?.email}%0D%0ANome: ${user?.name}%0D%0A%0D%0AConfirmo esta solicitação.`;
                                    setShowDeleteModal(false);
                                }}
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Confirmar Solicitação de Exclusão
                            </Button>
                            <Button
                                variant="ghost"
                                className="w-full"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                Cancelar
                            </Button>
                        </div>
                    </Card>
                </div>
            )}

            {/* Footer for non-authenticated */}
            {!isAuthenticated && (
                <footer className="relative py-8 border-t border-white/10">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <p className="text-white/40 text-sm">
                            © 2026 Paula Pontvianne Consultoria em Comunicação Empática. Todos os direitos reservados.
                            <span className="ml-2 text-white/30">v4.0</span>
                        </p>
                    </div>
                </footer>
            )}
        </div>
    );
};

export default PrivacyPolicy;

